import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import Iconify from '../../theme/iconify/iconify';

import Pagination from '../util/Pagination';
import ConfirmationModal from '../util/ConfirmationModal';

function Pages({ document, setDocument, update }) {
    const [value, setValue] = React.useState(document.raw_pages_text[0]);
    const [rows, setRows] = React.useState(20);
    const [numPages, setNumPages] = React.useState(document.raw_pages_text.length);
    const [pageNumber, setPageNumber] = React.useState(1);
    const [removePageModal, setRemovePageModal] = React.useState(false);

    const { t } = useTranslation();

    React.useEffect(() => {
        if (!document) return;
        if (!pageNumber) return;
        setValue(document.raw_pages_text[pageNumber - 1]);
    }, [document, pageNumber]);

    React.useEffect(() => {
        setNumPages(document.raw_pages_text.length);
    }, [document]);

    function changePage(offset) {
        setPageNumber(prevPageNumber => prevPageNumber + offset);
    }

    const removePage = () => {
        const rawPages = [...document.raw_pages_text];
        rawPages.splice(pageNumber - 1, 1);
        if (pageNumber === numPages) changePage(-1);
        setDocument({ ...document, raw_pages_text: rawPages });
        update({
            _id: document._id,
            original: document.original,
            filename: document.filename,
            type: document.type,
            size: document.size,
            timestamp: document.timestamp,
            user: document.user,
            raw_pages_text: rawPages,
            raw_sentences_text: document.raw_sentences_text,
            embeddings: document.embeddings
        });
        setRemovePageModal(false);
    };

    return (
        <>
            {document.raw_pages_text.length !== 0 ?
                <Grid container justifyContent='center' alignItems='center'>
                    <Grid item xs={12} sx={{ position: 'relative' }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={rows}
                            value={value}
                            onChange={(e) => {
                                setValue(e.target.value);
                                const rawPages = [...document.raw_pages_text];
                                rawPages[pageNumber - 1] = e.target.value;
                                setDocument({ ...document, raw_pages_text: rawPages });
                            }}
                        />
                        <Grid
                            container
                            justifyContent='flex-end'
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                mt: 2,
                                mr: 2,
                                pb: 2,
                                zIndex: 1,
                                width: 'fit-content',
                                opacity: {
                                    xs: 1,
                                    lg: 0.4
                                },
                                '&:hover': {
                                    opacity: 1
                                }
                            }}
                        >
                            <Paper elevation={2} sx={{ border: (theme) => `solid 1px ${theme.palette.divider}` }}>
                                <Tooltip title={t('document.tabs.pages.tooltips.remove')} arrow>
                                    <IconButton
                                        color='error'
                                        onClick={() => setRemovePageModal(true)}
                                    >
                                        <Iconify icon='eva:trash-outline' />
                                    </IconButton>
                                </Tooltip>
                                <Box
                                    component='span'
                                    sx={{
                                        display: 'inline',
                                        alignSelf: 'center',
                                        fontSize: '0.875rem',
                                        borderRight: (theme) => `solid 2px ${theme.palette.divider}`,
                                    }}
                                />
                                <Tooltip title={t('document.tabs.pages.tooltips.save')} arrow>
                                    <IconButton
                                        color='primary'
                                        onClick={() => update(document)}
                                    >
                                        <Iconify icon='carbon:save' />
                                    </IconButton>
                                </Tooltip>
                            </Paper>
                        </Grid>
                        <Pagination page={pageNumber} total={numPages} changePage={changePage} />
                    </Grid>
                </Grid>
                :
                <Grid item xs={12}>
                    <Typography variant='body2'>{t('document.noPages')}</Typography>
                </Grid>
            }
            <ConfirmationModal
                type='remove'
                title={t('document.modals.removePage.title')}
                description={t('document.modals.removePage.description')}
                confirmationText={t('document.modals.removePage.confirmation')}
                confirm={removePage}
                open={removePageModal}
                onClose={() => setRemovePageModal(false)}
            />
        </>
    );
}

export default Pages;